import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";
import { createCurrencyOption } from "@/hooks/useCurrencyOption";
import { countries_options } from "@/components/Form/CountryPhone/data";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import { z } from "zod";
import { useTranslation } from "react-i18next";

const TABLE_NAME = Tables.C5_PORTFOLIOS;

const filterC5PortfoliosSchema = z.object({
  client_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  vehicle_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  account_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  currency_contains: z.string().optional(),
  reporting_currency_contains: z.string().optional(),
  bank_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  bank_reporting_currency_contains: z.string().optional(),
  bank_country_contains: z.string().optional(),
  account_type_contains: z.string().optional(),
  investment_type_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  contract_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  code_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  collateral_contains: z.string().optional(),
});

export type FilterC5PortfoliosSchema = z.infer<typeof filterC5PortfoliosSchema>;
const filterC5PortfoliosFields = filterC5PortfoliosSchema.keyof().enum;

const portfolioDepFields: readonly DepField<FilterC5PortfoliosSchema>[] = [
  {
    name: filterC5PortfoliosFields.bank_name_contains,
    key: "bank_name",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterC5PortfoliosFields.client_contains,
    key: "client",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterC5PortfoliosFields.vehicle_contains,
    key: "vehicle",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterC5PortfoliosFields.investment_type_contains,
    key: "investment_type",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterC5PortfoliosFields.contract_name_contains,
    key: "contract_name",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterC5PortfoliosFields.name_contains,
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  {
    name: filterC5PortfoliosFields.code_contains,
    key: "code",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterC5PortfoliosFields.account_name_contains,
    key: "account_name",
    extract: "text",
    bucket: "equals",
  },
];

const client = Clients.C5;

export function FilterC5Portfolios() {
  const { t, i18n } = useTranslation("tables");

  const fields: Field[] = [
    {
      name: filterC5PortfoliosFields.client_contains,
      label: t(`${TABLE_NAME}.searchbox.client_contains`, { defaultValue: "Client" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C5}/${Tables.C5_PORTFOLIOS}`,
      column_name: "client",
      filters: makeFiltersBuilder<FilterC5PortfoliosSchema>(
        portfolioDepFields,
        filterC5PortfoliosFields.client_contains,
      ),
    },
    {
      name: filterC5PortfoliosFields.vehicle_contains,
      label: t(`${TABLE_NAME}.searchbox.vehicle_contains`, { defaultValue: "Vehicle / Beneficiary" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C5}/${Tables.C5_PORTFOLIOS}`,
      column_name: "vehicle",
      filters: makeFiltersBuilder<FilterC5PortfoliosSchema>(
        portfolioDepFields,
        filterC5PortfoliosFields.vehicle_contains,
      ),
    },
    {
      name: filterC5PortfoliosFields.account_name_contains,
      label: t(`${TABLE_NAME}.searchbox.account_name_contains`, { defaultValue: "Account group" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C5}/${Tables.C5_PORTFOLIOS}`,
      column_name: "account_name",
      filters: makeFiltersBuilder<FilterC5PortfoliosSchema>(
        portfolioDepFields,
        filterC5PortfoliosFields.account_name_contains,
      ),
    },
    {
      name: filterC5PortfoliosFields.name_contains,
      label: t(`${TABLE_NAME}.searchbox.name_contains`, { defaultValue: "Account" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C5}/${Tables.C5_PORTFOLIOS}`,
      column_name: "name",
      filters: makeFiltersBuilder<FilterC5PortfoliosSchema>(
        portfolioDepFields,
        filterC5PortfoliosFields.name_contains,
      ),
    },
    {
      name: filterC5PortfoliosFields.currency_contains,
      label: t(`${TABLE_NAME}.searchbox.currency_contains`, { defaultValue: "Portfolio currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    {
      name: filterC5PortfoliosFields.reporting_currency_contains,
      label: t(`${TABLE_NAME}.searchbox.reporting_currency_contains`, { defaultValue: "Reporting currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    {
      name: filterC5PortfoliosFields.bank_name_contains,
      label: t(`${TABLE_NAME}.searchbox.bank_name_contains`, { defaultValue: "Bank" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C5}/${Tables.C5_PORTFOLIOS}`,
      column_name: "bank_name",
      filters: makeFiltersBuilder<FilterC5PortfoliosSchema>(
        portfolioDepFields,
        filterC5PortfoliosFields.bank_name_contains,
      ),
    },
    {
      name: filterC5PortfoliosFields.account_type_contains,
      label: t(`${TABLE_NAME}.searchbox.account_type_contains`, { defaultValue: "Account type" }),
      type: "select",
      useTranslation: true,
      client: Clients.C5,
      table_name: Tables.C5_PORTFOLIOS,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: "account_type",
    },
    {
      name: filterC5PortfoliosFields.bank_reporting_currency_contains,
      label: t(`${TABLE_NAME}.searchbox.bank_reporting_currency_contains`, { defaultValue: "Management currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    {
      name: filterC5PortfoliosFields.bank_country_contains,
      label: t(`${TABLE_NAME}.searchbox.bank_country_contains`, { defaultValue: "Bank country" }),
      type: "autocomplete",
      options: countries_options,
    },
    {
      name: filterC5PortfoliosFields.investment_type_contains,
      label: t(`${TABLE_NAME}.searchbox.investment_type_contains`, { defaultValue: "Investment type" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C5}/${Tables.C5_PORTFOLIOS}`,
      column_name: "investment_type",
      filters: makeFiltersBuilder<FilterC5PortfoliosSchema>(
        portfolioDepFields,
        filterC5PortfoliosFields.investment_type_contains,
      ),
    },
    {
      name: filterC5PortfoliosFields.contract_name_contains,
      label: t(`${TABLE_NAME}.searchbox.contract_name_contains`, { defaultValue: "Contract name" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C5}/${Tables.C5_PORTFOLIOS}`,
      column_name: "contract_name",
      filters: makeFiltersBuilder<FilterC5PortfoliosSchema>(
        portfolioDepFields,
        filterC5PortfoliosFields.contract_name_contains,
      ),
    },
    {
      name: filterC5PortfoliosFields.code_contains,
      label: t(`${TABLE_NAME}.searchbox.code_contains`, { defaultValue: "Code" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C5}/${Tables.C5_PORTFOLIOS}`,
      column_name: "code",
      filters: makeFiltersBuilder<FilterC5PortfoliosSchema>(
        portfolioDepFields,
        filterC5PortfoliosFields.code_contains,
      ),
    },
    {
      name: filterC5PortfoliosFields.collateral_contains,
      label: t(`${TABLE_NAME}.searchbox.collateral_contains`, { defaultValue: "Pledged securities account" }),
      type: "select",
      useTranslation: false,
      options: [
        { value: "true", label: "Oui" },
        { value: "false", label: "Non" },
      ],
    },
  ];

  return (
    <FilterTable
      client={client}
      schema={filterC5PortfoliosSchema}
      tableName={Tables.C5_PORTFOLIOS}
      fields={fields}
      columns={2}
    />
  );
}
